import { useState, useEffect } from 'react';
import { X } from 'lucide-react';

// iOS Safari/Chrome share icon — square with upward arrow
function IosShareIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2v12" />
      <path d="m5 9 7-7 7 7" />
      <rect x="3" y="12" width="18" height="10" rx="2" />
    </svg>
  );
}

// Détecte si on est sur iOS
function isIOS() {
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

function isChromeIOS() {
  return isIOS() && /CriOS/i.test(navigator.userAgent);
}

// Détecte si l'app est déjà installée (mode standalone)
function isInstalled() {
  return window.matchMedia('(display-mode: standalone)').matches
    || (window.navigator as any).standalone === true;
}

export function InstallBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const [installState, setInstallState] = useState<'idle' | 'installing' | 'installed'>('idle');
  const [progress, setProgress] = useState(0);
  const [dismissed, setDismissed] = useState(() =>
    localStorage.getItem('pwa-banner-dismissed') === 'true'
  );

  useEffect(() => {
    // Listen for appinstalled event
    const handleAppInstalled = () => {
      setProgress(100);
      setTimeout(() => {
        setInstallState('installed');
      }, 500);
    };

    window.addEventListener('appinstalled', handleAppInstalled);
    return () => window.removeEventListener('appinstalled', handleAppInstalled);
  }, []);

  useEffect(() => {
    // Si déjà installé ou déjà fermé → ne pas montrer
    if (isInstalled() || dismissed) return;

    if (isIOS()) {
      // iOS : on montre les instructions manuelles
      setShowBanner(true);
      setShowIOSInstructions(true);
      return;
    }

    // Check if prompt was already captured globally
    if ((window as any).deferredInstallPrompt) {
      setDeferredPrompt((window as any).deferredInstallPrompt);
      setShowBanner(true);
    }

    // Android/Chrome : écouter l'événement d'installation
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowBanner(true);
    };

    const customHandler = (e: Event) => {
      setDeferredPrompt((e as CustomEvent).detail);
      setShowBanner(true);
    };

    window.addEventListener('beforeinstallprompt', handler);
    window.dispatchEvent(new CustomEvent('appbeforeinstallprompt', { detail: (window as any).deferredInstallPrompt }));
    window.addEventListener('appbeforeinstallprompt', customHandler as any);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appbeforeinstallprompt', customHandler as any);
    };
  }, [dismissed]);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setInstallState('installing');
        setProgress(15);
        (window as any).deferredInstallPrompt = null;
        setDeferredPrompt(null);
        
        // Progress animation
        const interval = setInterval(() => {
          setProgress((prev) => {
            if (prev >= 90) {
              clearInterval(interval);
              return 90;
            }
            return prev + 15;
          });
        }, 150);
      }
    } catch (err) {
      console.error('PWA banner install error:', err);
    }
  };

  const handleOpenApp = () => {
    alert("L'application Bip Teams est maintenant installée sur votre appareil ! Vous pouvez la lancer depuis votre écran d'accueil.");
    setShowBanner(false);
  };

  const handleDismiss = () => {
    setShowBanner(false);
    setDismissed(true);
    localStorage.setItem('pwa-banner-dismissed', 'true');
  };

  if (!showBanner) return null;

  return (
    <div className="flex-none z-50 animate-slide-down">
      <div className="bg-indigo-600/95 backdrop-blur-md border-b border-indigo-500/50 px-4 py-3 shadow-lg shadow-indigo-500/20">
        <div className="flex items-start gap-3 max-w-sm mx-auto">
          {/* Logo Bip Teams */}
          <div className="flex-shrink-0 mt-0.5">
            <div className="w-9 h-9 rounded-xl overflow-hidden bg-[#14161B] border border-indigo-500/30 flex items-center justify-center">
              <img src="/icons/icon-192.png" alt="Bip Teams" className="w-7 h-7 object-contain" />
            </div>
          </div>
          
          {/* Content */}
          <div className="flex-1 min-w-0">
            {showIOSInstructions ? (
              <>
                <p className="text-white font-bold text-sm">Installer Bip Teams</p>
                <p className="text-indigo-200 text-xs mt-0.5 leading-relaxed">
                  {isChromeIOS() ? (
                    <>Appuyez sur <IosShareIcon className="w-3 h-3 inline text-white" /> <strong>Partager</strong> dans Chrome puis{' '}
                    <strong>"Ajouter à l'écran d'accueil"</strong>.</>
                  ) : (
                    <>Appuyez sur <IosShareIcon className="w-3 h-3 inline text-white" /> <strong>Partager</strong> dans Safari puis{' '}
                    <strong>"Sur l'écran d'accueil"</strong> pour installer Bip Teams.</>
                  )}
                </p>
              </>
            ) : (
              <>
                <p className="text-white font-bold text-sm">
                  {installState === 'installed' ? 'Bip Teams installé !' : 'Installer Bip Teams'}
                </p>
                <p className="text-indigo-200 text-xs mt-0.5">
                  {installState === 'installed' 
                    ? "L'application est prête et disponible sur votre écran d'accueil."
                    : "Accédez rapidement à votre espace de travail depuis l'écran d'accueil."}
                </p>
                
                {/* Progress bar */}
                {installState === 'installing' && (
                  <div className="w-full bg-indigo-800 rounded-full h-1.5 mt-2 overflow-hidden">
                    <div 
                      className="bg-white h-1.5 rounded-full transition-all duration-300 ease-out" 
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                )}
                
                {/* Buttons */}
                {installState === 'idle' && (
                  <button
                    onClick={handleInstall}
                    className="mt-2 px-3 py-1.5 bg-white text-indigo-600 text-xs font-bold rounded-lg hover:bg-indigo-50 transition-colors"
                  >
                    Installer
                  </button>
                )}
                
                {installState === 'installed' && (
                  <button
                    onClick={handleOpenApp}
                    className="mt-2 px-3 py-1.5 bg-green-500 text-white text-xs font-bold rounded-lg hover:bg-green-600 transition-colors"
                  >
                    Ouvrir l'application
                  </button>
                )}
              </>
            )}
          </div>
          
          {/* Dismiss button */}
          <button
            onClick={handleDismiss}
            className="flex-shrink-0 p-1 text-indigo-200 hover:text-white rounded-lg hover:bg-white/10 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
